import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Anchor,
  LogOut, 
  RefreshCw, 
  Plus,
  Edit,
  Loader2,
  Phone,
  Mail,
  User,
  ArrowLeft,
  UserCheck,
  UserX,
  Banknote,
  History,
  ChevronDown,
  ChevronUp,
  X,
  Trash2,
  Car,
  Ship,
  Pencil
} from "lucide-react";
import axios from "axios";
import PhoneInput, { getLanguageFromCountryCode, getCountryFromPhone } from "@/components/PhoneInput";

import { API_URL } from "@/config/api";

export default function DriversPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [expandedDriver, setExpandedDriver] = useState(null);
  const [driverBookings, setDriverBookings] = useState({});
  const [loadingBookings, setLoadingBookings] = useState({});
  const [cashbackLoading, setCashbackLoading] = useState({});
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [cashAdjustModal, setCashAdjustModal] = useState(null); // {driverId, driverName, currentAmount}
  const [newCashAmount, setNewCashAmount] = useState("");
  const [cashAdjustReason, setCashAdjustReason] = useState("");
  const [adjustingCash, setAdjustingCash] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    language: "en",
    vehicle_brand: "",
    vehicle_model: "",
    license_plate: ""
  });

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchDrivers();
  }, [token, navigate]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/admin/drivers`, { headers });
      setDrivers(response.data.drivers);
      setSelectedDrivers([]);
    } catch (error) {
      console.error("Failed to fetch drivers:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load drivers");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverBookings = async (driverId) => {
    setLoadingBookings(prev => ({ ...prev, [driverId]: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/admin/drivers/${driverId}/bookings`, { headers });
      setDriverBookings(prev => ({ ...prev, [driverId]: response.data }));
    } catch (error) {
      console.error("Failed to fetch driver bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoadingBookings(prev => ({ ...prev, [driverId]: false }));
    }
  };

  const handleCashback = async (driverId, driverName) => {
    const driver = drivers.find(d => d.id === driverId);
    const amount = driver?.cash_collected || 0;
    
    if (amount === 0) {
      toast.info("No cash to collect");
      return;
    }
    
    if (!window.confirm(`Confirm cashback of €${amount.toFixed(2)} from ${driverName}?`)) {
      return;
    }
    
    setCashbackLoading(prev => ({ ...prev, [driverId]: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/drivers/${driverId}/cashback`, {}, { headers });
      toast.success(`Cashback of €${amount.toFixed(2)} recorded`);
      fetchDrivers();
    } catch (error) {
      console.error("Cashback failed:", error);
      toast.error("Cashback failed");
    } finally {
      setCashbackLoading(prev => ({ ...prev, [driverId]: false }));
    }
  };

  const openCashAdjustModal = (driver) => {
    setCashAdjustModal({
      driverId: driver.id,
      driverName: `${driver.first_name} ${driver.last_name}`,
      currentAmount: driver.cash_collected || 0
    });
    setNewCashAmount((driver.cash_collected || 0).toFixed(2));
    setCashAdjustReason("");
  };

  const handleCashAdjust = async () => {
    if (!cashAdjustModal) return;
    
    const newAmount = parseFloat(newCashAmount);
    if (isNaN(newAmount) || newAmount < 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setAdjustingCash(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_URL}/api/admin/drivers/${cashAdjustModal.driverId}/adjust-cash`,
        { new_amount: newAmount, reason: cashAdjustReason },
        { headers }
      );
      toast.success(`Cash adjusted to €${newAmount.toFixed(2)}`);
      setCashAdjustModal(null);
      fetchDrivers();
    } catch (error) {
      console.error("Cash adjustment failed:", error);
      toast.error("Failed to adjust cash");
    } finally {
      setAdjustingCash(false);
    }
  };

  const toggleDriverExpand = async (driverId) => {
    if (expandedDriver === driverId) {
      setExpandedDriver(null);
    } else {
      setExpandedDriver(driverId);
      if (!driverBookings[driverId]) {
        fetchDriverBookings(driverId);
      }
    }
  };

  const toggleSelectDriver = (id) => {
    setSelectedDrivers(prev => 
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const activeDrivers = drivers.filter(d => d.active);
    if (selectedDrivers.length === activeDrivers.length) {
      setSelectedDrivers([]);
    } else {
      setSelectedDrivers(activeDrivers.map(d => d.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDrivers.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to deactivate ${selectedDrivers.length} driver(s)? This will NOT affect their booking history.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/drivers/bulk-delete`, { ids: selectedDrivers }, { headers });
      toast.success(`${selectedDrivers.length} driver(s) deactivated`);
      fetchDrivers();
    } catch (error) {
      console.error("Bulk delete failed:", error);
      toast.error("Failed to deactivate drivers");
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingDriver) {
        await axios.put(`${API_URL}/api/admin/drivers/${editingDriver.id}`, formData, { headers });
        toast.success("Driver updated successfully");
      } else {
        await axios.post(`${API_URL}/api/admin/drivers`, formData, { headers });
        toast.success("Driver added successfully");
      }
      
      setShowForm(false);
      setEditingDriver(null);
      setFormData({ first_name: "", last_name: "", email: "", phone_number: "", language: "en", vehicle_brand: "", vehicle_model: "", license_plate: "" });
      fetchDrivers();
    } catch (error) {
      console.error("Failed to save driver:", error);
      toast.error(error.response?.data?.detail || "Failed to save driver");
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      first_name: driver.first_name,
      last_name: driver.last_name,
      email: driver.email,
      phone_number: driver.phone_number,
      language: driver.language || "en",
      vehicle_brand: driver.vehicle_brand || "",
      vehicle_model: driver.vehicle_model || "",
      license_plate: driver.license_plate || ""
    });
    setShowForm(true);
  };

  const handleDeactivate = async (driverId) => {
    if (!window.confirm("Are you sure you want to deactivate this driver?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/admin/drivers/${driverId}`, { headers });
      toast.success("Driver deactivated");
      fetchDrivers();
    } catch (error) {
      console.error("Failed to deactivate driver:", error);
      toast.error("Failed to deactivate driver");
    }
  };

  const handleReactivate = async (driverId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_URL}/api/admin/drivers/${driverId}`, { active: true }, { headers });
      toast.success("Driver reactivated");
      fetchDrivers();
    } catch (error) {
      console.error("Failed to reactivate driver:", error);
      toast.error("Failed to reactivate driver");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const activeDrivers = drivers.filter(d => d.active);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-black text-white tracking-tight">Y GO</span>
              <span className="hidden sm:inline text-xs text-slate-400 border-l border-slate-700 pl-3">Driver Management</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/admin/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white px-2 sm:px-4">
                  <ArrowLeft className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white px-2 sm:px-4">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Drivers ({drivers.length})</h1>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={fetchDrivers}>
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            {selectedDrivers.length > 0 && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleBulkDelete}
                disabled={deleting}
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin sm:mr-2" /> : <Trash2 className="w-4 h-4 sm:mr-2" />}
                <span className="hidden sm:inline">Deactivate ({selectedDrivers.length})</span>
                <span className="sm:hidden">{selectedDrivers.length}</span>
              </Button>
            )}
            <Button onClick={() => {
              setEditingDriver(null);
              setFormData({ first_name: "", last_name: "", email: "", phone_number: "", language: "en", vehicle_brand: "", vehicle_model: "", license_plate: "" });
              setShowForm(true);
            }} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Driver</span>
            </Button>
          </div>
        </div>

        {/* Select All */}
        {activeDrivers.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <Checkbox
              checked={selectedDrivers.length === activeDrivers.length && activeDrivers.length > 0}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-sm text-slate-600">Select all active drivers</span>
          </div>
        )}

        {/* Driver Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingDriver ? "Edit Driver" : "Add New Driver"}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone_number">Phone Number *</Label>
                    <PhoneInput
                      value={formData.phone_number}
                      onChange={(phone) => setFormData({...formData, phone_number: phone})}
                      onCountryChange={(country) => {
                        // Auto-assign language based on country code
                        if (!editingDriver) {
                          setFormData(prev => ({...prev, language: country.lang}));
                        }
                      }}
                      placeholder="6 98 18 28 92"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="language">Notification Language</Label>
                    <p className="text-xs text-slate-500 mb-1">Auto-detected from phone, but can be overridden</p>
                    <Select value={formData.language} onValueChange={(value) => setFormData({...formData, language: value})}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">🇬🇧 English</SelectItem>
                        <SelectItem value="fr">🇫🇷 Français</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Vehicle Information */}
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      Vehicle Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="vehicle_brand">Brand</Label>
                        <Input
                          id="vehicle_brand"
                          value={formData.vehicle_brand}
                          onChange={(e) => setFormData({...formData, vehicle_brand: e.target.value})}
                          placeholder="e.g. Mercedes"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="vehicle_model">Model</Label>
                        <Input
                          id="vehicle_model"
                          value={formData.vehicle_model}
                          onChange={(e) => setFormData({...formData, vehicle_model: e.target.value})}
                          placeholder="e.g. Vito"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label htmlFor="license_plate">License Plate</Label>
                      <Input
                        id="license_plate"
                        value={formData.license_plate}
                        onChange={(e) => setFormData({...formData, license_plate: e.target.value})}
                        placeholder="e.g. AB-123-CD"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600">
                      {editingDriver ? "Update" : "Add Driver"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Drivers List */}
        <div className="space-y-4">
          {drivers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No drivers yet. Add your first driver to get started.</p>
              </CardContent>
            </Card>
          ) : (
            drivers.map((driver) => (
              <Card key={driver.id} className={`${!driver.active ? "opacity-60" : ""} ${selectedDrivers.includes(driver.id) ? "ring-2 ring-orange-500" : ""}`}>
                <CardContent className="py-4">
                  {/* Driver Info Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-3">
                      {driver.active && (
                        <Checkbox
                          checked={selectedDrivers.includes(driver.id)}
                          onCheckedChange={() => toggleSelectDriver(driver.id)}
                          className="mt-1"
                        />
                      )}
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shrink-0 ${driver.active ? 'bg-orange-100' : 'bg-slate-200'}`}>
                        <User className={`w-5 h-5 sm:w-6 sm:h-6 ${driver.active ? 'text-orange-600' : 'text-slate-400'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-slate-900 text-sm sm:text-base">
                            {driver.first_name} {driver.last_name}
                          </h3>
                          <Badge className={`text-xs shrink-0 ${driver.active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-800"}`}>
                            {driver.active ? "Active" : "Inactive"}
                          </Badge>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {driver.language === "fr" ? "🇫🇷" : "🇬🇧"}
                          </Badge>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1 truncate">
                            <Mail className="w-4 h-4 shrink-0" />
                            <span className="truncate">{driver.email}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4 shrink-0" />
                            {driver.phone_number}
                          </span>
                        </div>
                        {/* Vehicle Info */}
                        {(driver.vehicle_brand || driver.license_plate) && (
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 flex-wrap">
                            <Car className="w-3 h-3" />
                            {driver.vehicle_brand} {driver.vehicle_model} {driver.license_plate && `• ${driver.license_plate}`}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Cash & Actions */}
                    {/* Cash & Actions Container - Stack on mobile */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 ml-0 sm:ml-auto mt-3 sm:mt-0">
                      {/* Cash Collected Box */}
                      <div className="bg-emerald-50 rounded-lg p-3 sm:text-right">
                        <div className="flex items-center gap-2 sm:justify-end">
                          <Banknote className="w-4 h-4 text-emerald-600" />
                          <span className="text-xs text-slate-500">Cash to collect</span>
                          <button
                            onClick={() => openCashAdjustModal(driver)}
                            className="text-slate-400 hover:text-slate-600"
                            title="Adjust cash amount"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-1">
                          <p className="text-xl font-bold text-emerald-600">
                            €{(driver.cash_collected || 0).toFixed(2)}
                          </p>
                          {(driver.cash_collected || 0) > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                              onClick={() => handleCashback(driver.id, `${driver.first_name} ${driver.last_name}`)}
                              disabled={cashbackLoading[driver.id]}
                            >
                              {cashbackLoading[driver.id] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                "Cash Back"
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 justify-end">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => toggleDriverExpand(driver.id)}
                          title="View bookings"
                        >
                          <History className="w-4 h-4 sm:mr-1" />
                          {expandedDriver === driver.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(driver)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {driver.active ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeactivate(driver.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleReactivate(driver.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <UserCheck className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Bookings */}
                  {expandedDriver === driver.id && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Booking History
                      </h4>
                      
                      {loadingBookings[driver.id] ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                        </div>
                      ) : driverBookings[driver.id]?.bookings?.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {driverBookings[driver.id].bookings.slice(0, 10).map((booking) => (
                            <div key={booking.booking_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                              <div className="flex items-center gap-3">
                                <span className="font-mono font-semibold text-orange-600">{booking.booking_id}</span>
                                <span className="flex items-center gap-1 text-slate-600">
                                  <Ship className="w-3 h-3" />
                                  {booking.ship_name}
                                </span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-600">{booking.terminal_name}</span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-500">{formatDate(booking.pickup_datetime)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  booking.payment_status === "paid" ? "bg-purple-100 text-purple-800" :
                                  booking.payment_status === "paid_by_cash" ? "bg-emerald-100 text-emerald-800" :
                                  "bg-amber-100 text-amber-800"
                                }>
                                  {booking.payment_status === "paid" ? "Online" : 
                                   booking.payment_status === "paid_by_cash" ? "Cash" : "Pending"}
                                </Badge>
                                <span className="font-semibold">€{booking.final_price}</span>
                              </div>
                            </div>
                          ))}
                          {driverBookings[driver.id].bookings.length > 10 && (
                            <p className="text-center text-sm text-slate-500 pt-2">
                              And {driverBookings[driver.id].bookings.length - 10} more...
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm py-4 text-center">No bookings yet</p>
                      )}
                      
                      {/* Stats Summary */}
                      {driverBookings[driver.id]?.stats && (
                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                          <div className="text-center">
                            <p className="text-xs text-slate-500">Total Bookings</p>
                            <p className="text-lg font-bold text-slate-700">{driverBookings[driver.id].total}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-500">Cash Collected</p>
                            <p className="text-lg font-bold text-emerald-600">€{driverBookings[driver.id].stats.total_cash_collected?.toFixed(0) || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-500">Online Payments</p>
                            <p className="text-lg font-bold text-purple-600">€{driverBookings[driver.id].stats.total_online_payments?.toFixed(0) || 0}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Cash Adjustment Modal */}
      {cashAdjustModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Adjust Cash Balance</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setCashAdjustModal(null)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Driver</p>
                  <p className="font-semibold">{cashAdjustModal.driverName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Current Amount</p>
                  <p className="text-2xl font-bold text-emerald-600">€{cashAdjustModal.currentAmount.toFixed(2)}</p>
                </div>
                <div>
                  <Label htmlFor="newAmount">New Amount (€)</Label>
                  <Input
                    id="newAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newCashAmount}
                    onChange={(e) => setNewCashAmount(e.target.value)}
                    placeholder="0.00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="reason">Reason (optional)</Label>
                  <Input
                    id="reason"
                    value={cashAdjustReason}
                    onChange={(e) => setCashAdjustReason(e.target.value)}
                    placeholder="e.g. Correction, partial cashback..."
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setCashAdjustModal(null)}>
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                    onClick={handleCashAdjust}
                    disabled={adjustingCash}
                  >
                    {adjustingCash ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Adjustment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
